<template>
  <div class="charts-grid" :class="{ 'is-clickable': clickable }">
    <div
      v-for="attr in attributes"
      :key="attr.name"
      class="chart-tile"
      :class="chartSizeClassFor(attr)"
    >
      <div class="card h-100">
        <div class="card-header-compact">
          <span class="card-title-compact" :title="formatAttributeName(attr.name)">
            {{ formatAttributeName(attr.name) }}
          </span>
          <span class="badge-compact">{{ attr.values.length }}</span>
        </div>
        <div class="card-body p-1">
          <div class="chart-container" :style="chartHeightFor(attr)">
            <component
              :is="chartTypeFor(attr)"
              v-if="attr.values.length > 0"
              :key="attr.name"
              :data="chartDataFor(attr)"
              :options="chartOptionsFor(attr)"
              :ref="el => setChartRef(attr.name, el)"
              @click="onChartClick($event, attr)"
            />
            <div v-else class="text-center text-muted py-4">
              <i class="fa fa-chart-bar fa-2x mb-2"></i>
              <p class="small mb-0">No data</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Bar, Doughnut, Pie } from 'vue-chartjs'
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  BarElement,
  ArcElement,
  CategoryScale,
  LinearScale,
} from 'chart.js'
import {
  buildChartData,
  buildChartOptions,
  chartHeightFor,
  chartSizeClassFor,
  chartTypeFor,
  chartValuesFor,
  formatAttributeName,
} from '../../utils/attributeCharts'

ChartJS.register(Title, Tooltip, Legend, BarElement, ArcElement, CategoryScale, LinearScale)

export default {
  name: 'AttributeCharts',
  components: {
    Bar,
    Doughnut,
    Pie,
  },
  props: {
    // [{ name, values: [{ value, count }] }], in the order they should appear
    attributes: {
      type: Array,
      default: () => [],
    },
    // [{ attribute, value }] to draw in the highlight colour
    selected: {
      type: Array,
      default: () => [],
    },
    // Off once the first render has played, so picking a value does not replay
    // every animation on the page
    animate: {
      type: Boolean,
      default: true,
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    // How many records the result covers, so a multi-select column can be shown
    // as a share of the audience rather than of its own inflated total
    recordTotal: {
      type: Number,
      default: 0,
    },
  },
  emits: ['select'],
  data() {
    return {
      chartRefs: {},
    }
  },
  computed: {
    darkMode() {
      return !!this.$store.state.darkMode
    },
  },
  methods: {
    chartSizeClassFor,
    chartHeightFor,
    chartTypeFor,
    formatAttributeName,
    chartDataFor(attr) {
      return buildChartData(attr, {
        isSelected: value => this.isValueSelected(attr.name, value),
      })
    },
    chartOptionsFor(attr) {
      return buildChartOptions(attr, {
        darkMode: this.darkMode,
        animate: this.animate,
        recordTotal: this.recordTotal,
      })
    },
    isValueSelected(attribute, value) {
      return this.selected.some(item => item.attribute === attribute && item.value === value)
    },
    setChartRef(attrName, el) {
      if (el) this.chartRefs[attrName] = el
    },
    onChartClick(event, attr) {
      if (!this.clickable) return
      const chart = this.chartRefs[attr.name]?.chart
      if (!chart) return

      const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, false)
      if (elements.length === 0) return

      const value = chartValuesFor(attr)[elements[0].index]?.value
      if (value === undefined) return

      this.$emit('select', { attribute: attr.name, value })
    },
  },
}
</script>

<style scoped>
/* Column count comes from whoever is hosting the grid - a full page has room for
   more of them than a modal does. Set --chart-columns on any ancestor to change
   it; the media queries below still win because they set it on the grid itself. */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(var(--chart-columns, 3), 1fr);
  gap: 0.5rem;
  max-width: 100%;
  box-sizing: border-box;
}

.chart-tile {
  min-height: 0;
  min-width: 0;
}

.chart-tile .card {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;
}

.chart-tile .card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}

.chart-tile.chart-xs,
.chart-tile.chart-sm,
.chart-tile.chart-md {
  grid-column: span 1;
}

.chart-tile.chart-lg,
.chart-tile.chart-xl {
  grid-column: span 2;
}

.card-header-compact {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.35rem 0.5rem;
  background: linear-gradient(135deg, #f8f9fa, #eef1f4);
  border-bottom: 1px solid #e9ecef;
}

.card-title-compact {
  font-size: 0.7rem;
  font-weight: 600;
  color: #495057;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.badge-compact {
  font-size: 0.6rem;
  font-weight: 500;
  background: #6c757d;
  color: white;
  padding: 0.15rem 0.35rem;
  border-radius: 3px;
  margin-left: 0.25rem;
  flex-shrink: 0;
}

.chart-container {
  position: relative;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.is-clickable .chart-container {
  cursor: pointer;
}

.is-clickable .chart-container:hover {
  opacity: 0.95;
}

@media (max-width: 1200px) {
  .charts-grid {
    --chart-columns: 3;
  }
}

@media (max-width: 992px) {
  .charts-grid {
    --chart-columns: 2;
  }
}

@media (max-width: 576px) {
  .charts-grid {
    --chart-columns: 1;
  }

  .chart-tile {
    grid-column: span 1 !important;
  }
}
</style>

<style lang="scss">
@use '../../theme/scss/_user-variables.scss' as *;

body.dark-mode {
  .chart-tile .card {
    background-color: $dark-elevated-bg !important;
    border-color: $dark-border-color !important;
  }
  .card-header-compact {
    background: linear-gradient(to right, $dark-border-color 100%, $dark-border-color 100%) !important;
    border-bottom-color: $dark-border-color !important;
  }
  .card-title-compact {
    color: $dark-text-primary !important;
  }
}
</style>
